import { useState } from 'react';
import { Bell, BellOff, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  useCreateConnection,
  useMyConnections,
} from '../../hooks/useConnections';
import {
  useSnoozeChef,
  useSnoozedChefs,
  useUnsnoozeChef,
} from '../../hooks/useNotificationMutes';
import { notify } from '../../services/notify';
import { useProfileStore } from '../../store/useProfileStore';
import { effectiveConnectionType, isFromQrCode } from '../../types/connection';
import { ConnectionTypeModal } from '../profile/ConnectionTypeModal';
import { Avatar } from '../ui/Avatar';
import { Modal } from '../ui/Modal';
import type { UserAttributionSummary } from '../ui/UserAttribution';

interface ConnectionStatusModalProps {
  summary: UserAttributionSummary;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Tapping a display name (the canonical `UserAttribution`) opens this panel: it
 * tells the viewer how close they are to that person and — when they're not
 * already a direct (L1) connection — lets them send a connection request in one
 * tap. This is the third connection entry point alongside share links and the
 * email-driven "Connect with Friends" flow.
 *
 * Status copy maps to `summary.connectionLevel`:
 *   - 1            → "Your connection" (no Send; already connected). If the
 *                    viewer initiated the connection, a re-categorize pill
 *                    appears — the same initiator-only affordance as the
 *                    profile connections list.
 *   - 2            → "Somebody you know's connection" + Send
 *   - 3 / null     → "You are not currently connected" + Send
 *
 * Pending detection: we fetch the caller's outgoing requests (lazily, only
 * while open) and, if one already targets this profile, show "Request pending"
 * instead of the Send button. The send mutation is idempotent server-side, so
 * even a racey re-send is harmless — this is purely a nicer UX.
 */
export const ConnectionStatusModal: React.FC<ConnectionStatusModalProps> = ({
  summary,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation('connections');
  const createConnection = useCreateConnection();
  const selfId = useProfileStore((s) => s.activeProfile?.id);
  const [typeModalOpen, setTypeModalOpen] = useState(false);

  const isSelf = !!summary.id && summary.id === selfId;

  const level = summary.connectionLevel;
  const name = summary.displayName || summary.businessName || '';
  const isConnected = level === 1;

  // Lazy: only fetch outgoing requests once the panel is open (and never for
  // self — you can't connect to yourself).
  const { data: sentRequests, isLoading: sentLoading } = useMyConnections(
    'requests_from_me',
    { enabled: isOpen && !isSelf },
  );

  // For an existing (L1) connection we mirror the profile connections row:
  // avatar + name over the type. Re-categorizing is initiator-only, so we fetch
  // the accepted rows lazily and find the one between me and this person in
  // either direction — the initiator gets the switcher pill, the recipient a
  // static type label.
  const { data: accepted } = useMyConnections('accepted', {
    enabled: isOpen && !isSelf && isConnected,
  });
  const myConnection = (accepted ?? []).find(
    (c) =>
      (c.profileId === selfId && c.connectedProfileId === summary.id) ||
      (c.connectedProfileId === selfId && c.profileId === summary.id),
  );
  const isInitiator = !!myConnection && myConnection.profileId === selfId;
  // Campaign-driven rows have server-locked types (PATCH 400s), so hide the
  // chooser affordance to match — same rule as the profile connections row.
  const canRetype =
    isInitiator && !!myConnection && !isFromQrCode(myConnection);
  // Self and L1 need no request; everyone else (L2 / L3 / unknown) can send one.
  const canSend = !isSelf && !isConnected && !!summary.id;

  // Per-chef new-dish snooze. Only meaningful for people whose posts could
  // actually reach this viewer — L1 and L2 (see the fan-out audience). Fetch the
  // snoozed list lazily while open to know the current state for this chef.
  const showSnooze = !isSelf && !!summary.id && (level === 1 || level === 2);
  const { data: snoozedChefs } = useSnoozedChefs({
    enabled: isOpen && showSnooze,
  });
  const isSnoozed = (snoozedChefs ?? []).some(
    (m) => m.mutedProfileId === summary.id,
  );
  const snoozeChef = useSnoozeChef();
  const unsnoozeChef = useUnsnoozeChef();
  const snoozePending = snoozeChef.isPending || unsnoozeChef.isPending;

  const handleSnoozeToggle = (checked: boolean) => {
    if (!summary.id) return;
    if (checked) snoozeChef.mutate(summary.id);
    else unsnoozeChef.mutate(summary.id);
  };

  const title = isSelf
    ? t('status.titleSelf')
    : isConnected
      ? t('status.titleConnected')
      : level === 2
        ? t('status.titleFoF')
        : t('status.titleNone');

  const hasPending = (sentRequests ?? []).some(
    (c) =>
      c.connectedProfileId === summary.id && !c.acceptedAt && !c.rejectedAt,
  );

  const handleSend = async () => {
    if (!summary.id || createConnection.isPending) return;
    try {
      await createConnection.mutateAsync({ connectedProfileId: summary.id });
      // Confirm with a top toast and dismiss the panel. (The ['connections']
      // invalidation still refetches requests_from_me in the background so the
      // panel reflects "Request pending" if it's reopened.)
      notify.success(t('status.requestSentToast'));
      onClose();
    } catch {
      // Server-side errors (e.g. self-connect) surface via the global error
      // modal, same as the email flow. Keep the panel open.
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      {/* New-dish snooze toggle — sits just left of the Modal's built-in close
          X. Bell = alerts on (tap to snooze); bell-off = snoozed (tap to
          restore). Brand accent marks the ON state, matching how accent
          color reads everywhere else; snoozed goes muted. */}
      {showSnooze && (
        <button
          type="button"
          onClick={() => handleSnoozeToggle(!isSnoozed)}
          disabled={snoozePending}
          aria-label={isSnoozed ? t('snooze.unsnooze') : t('snooze.snooze')}
          title={isSnoozed ? t('snooze.unsnooze') : t('snooze.snooze')}
          className={`absolute right-12 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full transition hover:bg-black/5 active:scale-95 disabled:opacity-50 ${
            isSnoozed ? 'text-muted-foreground' : 'text-brand-primary'
          }`}
        >
          {isSnoozed ? <BellOff size={18} /> : <Bell size={18} />}
        </button>
      )}

      {isConnected ? (
        // L1: mirror the profile connections row — avatar on the left, name
        // over the type. The initiator gets the re-categorize switcher pill;
        // the recipient (or while the row is still loading) sees a static
        // type label. Campaign group label (if any) sits on its own row
        // below, matching the profile connections row layout.
        <>
          <div className="flex items-center gap-3">
            <Avatar src={summary.avatarUrl ?? null} name={name} size="md" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground truncate">
                {name}
              </p>
              {canRetype && myConnection ? (
                <button
                  type="button"
                  onClick={() => setTypeModalOpen(true)}
                  aria-label={t('type.changeAria')}
                  className="mt-1 inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t(`type.${effectiveConnectionType(myConnection.type)}`)}
                  <ChevronDown className="h-3 w-3" />
                </button>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {myConnection
                    ? t(`type.${effectiveConnectionType(myConnection.type)}`)
                    : title}
                </p>
              )}
            </div>
          </div>
          {myConnection?.qrCode?.connectionGroup && (
            <p className="mt-2 text-xs italic text-muted-foreground line-clamp-2">
              {t('list.connectedThrough')}
              {myConnection.qrCode.connectionGroup}
            </p>
          )}
        </>
      ) : (
        <>
          <div className="flex items-center gap-3 mb-4">
            <Avatar src={summary.avatarUrl ?? null} name={name} size="lg" />
            <span className="text-base font-semibold text-foreground truncate">
              {name}
            </span>
          </div>

          <p className="text-sm text-muted-foreground">{title}</p>
        </>
      )}

      {canSend && (
        <div className="flex justify-end gap-2 mt-5">
          {hasPending ? (
            <span className="px-4 py-2 text-sm font-medium text-muted-foreground">
              {t('status.requestPending')}
            </span>
          ) : (
            <button
              type="button"
              onClick={handleSend}
              disabled={createConnection.isPending || sentLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-lg hover:opacity-90 disabled:opacity-50 transition-colors"
            >
              {createConnection.isPending
                ? t('modal.sending')
                : t('status.sendRequest')}
            </button>
          )}
        </div>
      )}

      {/* Stacked above this panel (z-[60]) so the picker isn't hidden behind
          the status modal's backdrop. */}
      {myConnection && (
        <ConnectionTypeModal
          isOpen={typeModalOpen}
          onClose={() => setTypeModalOpen(false)}
          connection={myConnection}
          zIndexClass="z-[70]"
        />
      )}
    </Modal>
  );
};
